import React, { FC, useState } from "react";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import MuiAccordionDetails from "@material-ui/core/AccordionDetails";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import MuiAccordion from "@material-ui/core/Accordion";
import Divider from "@material-ui/core/Divider";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { Grid, Typography } from "@material-ui/core";
import UploadFile from "../../../../../components/UploadFile/UploadFile";
import ViewOtherFees from "./ViewOtherFees";
import {
  CourseDetailType,
  IAccommodationFeeViewModel,
  IApplicationFormsFrontEndModel,
  IDetailFileFrontEndModel,
  IDetailsViewModel,
  IOtherFeesFrontEndModel,
} from "../../../types";
import OverflowTooltip from "../../../../../components/OverflowTooltip/OverflowTooltip";

const Accordion = withStyles({
  root: {
    padding: 0,
    position: "relative",
    backgroundColor: "rgba(0, 0, 0, 0.01)",
    borderRadius: ".9375rem",
    border: "solid 1px rgba(197,197,197,.3)",
    boxShadow: "none",
    "& + &": {
      marginTop: 16,
    },
    "&:before": {
      display: "none",
    },
  },
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    cursor: "pointer",
  },
})(MuiAccordionSummary);

const AccordionContent = withStyles(() => ({
  root: {
    display: "block",
  },
}))(MuiAccordionDetails);

const useStyles = makeStyles(() => ({
  stepContainer: {
    backgroundColor: "#f5f6f7",
    borderRadius: 12,
    padding: 16,
    "& + &": {
      marginTop: 16,
    },
    "&>div:first-child": {
      marginTop: "unset",
    },
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#383d4a",
    marginTop: 16,
  },
  detailsLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#383d4a",
    marginBottom: 12,
  },
  bigLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#383d4a",
    marginTop: 16,
  },
  content: {
    fontSize: 16,
    fontWeight: "normal",
    color: "#000",
    marginTop: 8,
    wordBreak: "break-word",
  },
  bigContent: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#61657e",
  },
  doubleColumn: {
    width: "50%",
    fontSize: 16,
    fontWeight: "normal",
    color: "#000",
    marginTop: 8,
  },
  doubleColumnBold: {
    width: "50%",
    fontSize: 14,
    fontWeight: "bold",
    color: "#383d4a",
    marginTop: 8,
  },
  doubleColumnContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  branch: {
    marginTop: 8,
    marginBottom: "unset",
    paddingLeft: 16,
  },
  divider: {
    marginTop: 16,
    color: "#dae1ed",
  },
  calcBoard: {
    backgroundColor: "#fff",
    minHeight: 90,
    border: "1px solid #dae1ed",
    marginTop: 16,
    borderRadius: 8,
    padding: "10px 16px",
  },
  calcBoardContent: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#383d4a",
    lineHeight: "34px",
  },
  tuition: {
    marginLeft: 50,
    textDecoration: "line-through",
  },
  otherFees: {
    marginLeft: 16,
    textDecoration: "line-through",
  },
  calcValue: {
    marginLeft: 24,
  },
  arrowIcon: {
    transform: "rotate(90deg)",
  },
  fileUploaderContainer: {
    "& > div > div > div": {
      padding: "16px 0 16px 0",
    },
    "& > div > div > div:last-child": {
      padding: "16px 0 0 0",
    },
  },
  accordionContent: {
    "& .table": {
      margin: "0.9em auto",
      display: "table",
    },
    "& .table table": {
      borderCollapse: "collapse",
      borderSpacing: 0,
      width: "100%",
      height: "100%",
    },
    "& .table table td": {
      minWidth: "2em",
      padding: ".4em",
      border: "1px solid hsl(0, 0%, 75%)",
    },
    "& .table table th": {
      minWidth: "2em",
      padding: ".4em",
      border: "1px solid hsl(0, 0%, 75%)",
      fontWeight: "bold",
      background: "hsla(0, 0%, 0%, 5%)",
    },
  },
  docsContainer: {
    marginLeft: 16,
    marginTop: 8,
  },
  docGridItem: {
    position: "relative",
    "&::before": {
      content: '""',
      width: 12,
      height: 12,
      backgroundColor: "#0676ed",
      borderRadius: 7,
      position: "absolute",
      bottom: 9,
      left: -16,
    },
  },
  doc: {
    width: 86,
  },
  applicationLink: {
    marginTop: 8,
  },
}));

interface IViewCourseProps {
  language: string;
  category: string;
  courseType: string;
  minAge: string;
  maxAge?: string;
  branches: string[];
  requiredDocs: string[];
  applicationForms?: IApplicationFormsFrontEndModel[];
  applicationLink?: string;
  courseDuration: string;
  housingTypes?: number[];
  restrictedCountries?: string[];
  price: string;
  priceDuration: string;
  commissionAmount: string;
  otherFees?: IOtherFeesFrontEndModel[];
  accommodationFees?: IAccommodationFeeViewModel[];
  priceDiscount: string;
  otherFeesSum: string;
  otherFeesDiscount: string;
  calculatedPrice: string;
  calculatedOtherFees: string;
  hasPriceSupport: boolean;
  hasOtherFeesSupport: boolean;
  details: IDetailsViewModel[];
  detailFiles?: IDetailFileFrontEndModel[];
}

const ViewCourse: FC<IViewCourseProps> = (props) => {
  const classes = useStyles();

  const [expanded, setExpanded] = useState<CourseDetailType>();

  const handleChange = (panel) => (_, newExpanded) => {
    setExpanded(newExpanded ? panel.type : false);
  };

  const {
    language,
    category,
    courseType,
    minAge,
    maxAge,
    branches,
    requiredDocs,
    applicationForms,
    applicationLink,
    courseDuration,
    housingTypes,
    restrictedCountries,
    price,
    commissionAmount,
    priceDuration,
    accommodationFees,
    otherFees,
    priceDiscount,
    otherFeesSum,
    otherFeesDiscount,
    calculatedPrice,
    calculatedOtherFees,
    hasPriceSupport,
    hasOtherFeesSupport,
    details,
    detailFiles,
  } = props;

  return (
    <div>
      <div className={classes.stepContainer}>
        <div className={classes.label}>Language</div>
        <div className={classes.content}>{language}</div>
        <div className={classes.label}>Category</div>
        <div className={classes.content}>{category}</div>
        <div className={classes.label}>Course Type</div>
        <div className={classes.content}>{courseType}</div>
        <div className={classes.label}>Age Range</div>
        <div className={classes.doubleColumnContainer}>
          <div className={classes.doubleColumn}>Minimum Age: {minAge}</div>
          <div className={classes.doubleColumn}>Maximum Age: {maxAge}</div>
        </div>
        <div className={classes.label}>Branches</div>
        <ul className={classes.branch}>
          {branches.map((branch, i) => (
            <li key={branch + i} className={classes.content}>
              {branch}
            </li>
          ))}
        </ul>
      </div>
      <div className={classes.stepContainer}>
        <div className={classes.label}>Required Documents</div>
        <Grid container spacing={1} className={classes.docsContainer}>
          {requiredDocs.map((doc) => (
            <Grid key={doc} item xs={3} className={classes.docGridItem}>
              <div className={classes.doc}>
                <OverflowTooltip text={doc} />
              </div>
            </Grid>
          ))}
        </Grid>
        {!!applicationForms?.length && (
          <>
            <div className={classes.label}>Application File</div>
            <div className={classes.fileUploaderContainer}>
              <UploadFile isViewMode uploadedFiles={applicationForms} />
            </div>
          </>
        )}
        {!!applicationLink && (
          <>
            <div className={classes.label}>Application Link</div>
            <div></div>
            <div className={classes.applicationLink}>
              <a className="wedu-link" href={applicationLink} target="_blank">
                See the link
              </a>
            </div>
          </>
        )}
        <div className={classes.label}>Course Duration</div>
        <div className={classes.content}>{courseDuration}</div>
        {!!housingTypes?.length && (
          <>
            <div className={classes.label}>
              Housing Type{housingTypes.length > 1 ? "s" : ""}
            </div>
            <div className={classes.content}>{housingTypes.join(", ")}</div>
          </>
        )}
        {!!restrictedCountries?.length && (
          <>
            <div className={classes.label}>Restricted Countries</div>
            <ul className={classes.branch}>
              {restrictedCountries.map((r, i) => (
                <li key={r + i} className={classes.content}>
                  {r}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className={classes.stepContainer}>
        <div className={classes.label}>Price</div>
        <div className={classes.doubleColumnContainer}>
          <div className={classes.doubleColumn}>Price: {price}</div>
          <div className={classes.doubleColumn}>
            Commission: {commissionAmount}
          </div>
        </div>
        <div className={classes.content}>Duration: {priceDuration}</div>
        {!!accommodationFees?.length && (
          <>
            <div className={classes.label}>Accommodation Fees</div>
            {accommodationFees.map((fee) => (
              <div key={fee.label} className={classes.content}>
                {fee.label}: {fee.amount}
              </div>
            ))}
          </>
        )}
        {!!otherFees?.length && <ViewOtherFees otherFees={otherFees} />}
        <Divider className={classes.divider} />
        <div className={classes.bigLabel}>
          Price: <span className={classes.bigContent}>{price}</span>
        </div>
        <div className={classes.doubleColumnContainer}>
          <div className={classes.doubleColumnBold}>
            Discount: {priceDiscount}
          </div>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.bigLabel}>
          Other Fees: <span className={classes.bigContent}>{otherFeesSum}</span>
        </div>
        <div className={classes.doubleColumnContainer}>
          <div className={classes.doubleColumnBold}>
            Discount: {otherFeesDiscount}
          </div>
        </div>
        <div className={classes.calcBoard}>
          <div className={classes.calcBoardContent}>
            Price:
            {hasPriceSupport && (
              <span className={classes.tuition}>{price}</span>
            )}
            <span className={classes.calcValue}>{calculatedPrice}</span>
          </div>
          <div className={classes.calcBoardContent}>
            Other Fees:
            {hasOtherFeesSupport && (
              <span className={classes.otherFees}>{otherFeesSum}</span>
            )}
            <span className={classes.calcValue}>{calculatedOtherFees}</span>
          </div>
        </div>
      </div>
      <div className={classes.stepContainer}>
        <div>
          <div className={classes.detailsLabel}>Course Details</div>
          {details.map((item) => (
            <Accordion
              expanded={expanded === item.type}
              onChange={handleChange(item)}
              key={item.type}
            >
              <AccordionSummary
                expandIcon={<ArrowRightIcon className={classes.arrowIcon} />}
              >
                <Typography>{item.typeName}</Typography>
              </AccordionSummary>
              <AccordionContent>
                <div
                  dangerouslySetInnerHTML={{ __html: item.content }}
                  className={classes.accordionContent}
                />
              </AccordionContent>
            </Accordion>
          ))}
          {!!detailFiles?.length && (
            <>
              <div className={classes.label}>Uploaded Documents/Flyers</div>
              <div className={classes.fileUploaderContainer}>
                <UploadFile isViewMode uploadedFiles={detailFiles} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewCourse;
